import { Dialog, Disclosure, Transition } from "@headlessui/react";
import moment from "moment";
import parse from "html-react-parser";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { startMeeting } from "../../api/meetings";
import Partials from "../partials";
import Toggle from "../../assets/images/modal-toggle.svg";
import { getVirtualClasses } from "../../utils/reactQueries";
import _ from "lodash";
import UTILS from "../../utils";
import Blocks from ".";

const TabAction = {
  TODAY: "today",
  TOMORROW: "tomorrow",
  ALL: "all",
};

const Action = {
  RESCHEDULE: "reschedule",
  DELETE: "delete",
  CANCEL: "cancel",
};

const BlockTeacherLiveClass = () => {
  const history = useHistory();

  const [classes, setClasses] = useState([]);
  const [selectedAction, setSelectedAction] = useState("");
  const [filterData, setFilterData] = useState([]);
  const [activeTab, setActiveTab] = useState("today");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedClassDetails, setSelectedClassDetails] = useState({});

  const queryClient = useQueryClient();
  const { status, data: classData, error, isFetching } = getVirtualClasses();

  useEffect(() => {
    queryClient.invalidateQueries("getAllVirtualClassesRTQ");
  }, []);

  useEffect(() => {
    const classList = classData?.data?.body;
    if (classList?.length > 0) {
      setClasses(classList);
    }
  }, [classData]);

  useEffect(() => {
    if (classes.length) {
      handleFilter(activeTab);
    }
  }, [classes]);

  const [selectedRow, _selectedRow] = useState(0);
  const startingClassIdRef = useRef(null);

  const {
    mutate,
    isLoading: isMeetingStarting,
    isError: isMeetingStartError,
    data: meetingData,
    error: meetingError,
  } = useMutation(startMeeting, {
    onSuccess: (data) => {
      if (data?.data?.body?.start_url) {
        let startUrl = data.data.body.start_url;
        // Append buddingClassId as a URL fragment (#) so it doesn't affect BBB's checksum validation
        // Fragments are never sent to the server, but the BBB frontend JS can read them via window.location.hash
        if (startingClassIdRef.current) {
          startUrl = `${startUrl}#buddingClassId=${encodeURIComponent(startingClassIdRef.current)}`;
          console.log(`[BuddingFrontend] Appended buddingClassId=${startingClassIdRef.current} to start_url (as fragment)`);
        }
        window.open(startUrl, "_self");
      }
    },
  });

  const handleStartClick = (classId, index) => {
    startingClassIdRef.current = classId;
    mutate({ classId });
    _selectedRow(index);
  };

  const goToEditVirtualClass = (classId) => {
    localStorage.setItem("classId", classId);
    history.push(`/create-virtual-class/${classId}/edit`);
  };

  //filter the date ascending order
  const showAllFilters = () => {
    const sortedDatesAsc = _.sortBy(classes, (item) =>
      moment.unix(item?.start_time).date()
    );

    if (sortedDatesAsc?.length > 0) {
      const modifyData = sortedDatesAsc.filter((item) => {
        if (item?.student_count > 0) {
          return item;
        }
      });
      if (modifyData.length) {
        setActiveTab(TabAction.ALL);
      }
      setFilterData(modifyData);
    }
  };

  //filter the today class
  const todayClass = (isInitialLoading) => {
    const currentDate = moment().date();

    const modifyData = classes.filter((item) => {
      if (
        moment.unix(item?.start_time).date() === currentDate &&
        item?.student_count > 0
      ) {
        return item;
      }
    });

    if (!modifyData.length && isInitialLoading) {
      tomorrowClass(isInitialLoading);
    } else {
      setFilterData(modifyData);
    }
  };
  //filter the tomorrow class
  const tomorrowClass = (isInitialLoading) => {
    const currentDate = moment().clone().add(1, "d").date();
    const modifyData = classes.filter((item) => {
      if (
        moment.unix(item?.start_time).date() === currentDate &&
        item?.student_count > 0
      ) {
        return item;
      }
    });

    if (!modifyData.length && isInitialLoading) {
      showAllFilters();
    } else {
      setFilterData(modifyData);
      setActiveTab(TabAction.TOMORROW);
    }
  };

  const handleFilter = (filter, isInitialLoading = true) => {
    setActiveTab(filter);
    if (filter === TabAction.ALL) {
      showAllFilters();
    } else if (filter === TabAction.TODAY) {
      todayClass(isInitialLoading);
    } else if (filter === TabAction.TOMORROW) {
      tomorrowClass(isInitialLoading);
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setSelectedAction("");
  };

  const modifyClassPayload = (classDetails) => {
    const start_time = moment
      .unix(classDetails.start_time)
      .format("YYYY MM DD hh:mm A");
    const duration = UTILS.virtualClass
      .durationOptions()
      .find((dur) => dur.value === classDetails.duration_seconds);

    const modifyValue = {
      ...classDetails,
      title: classDetails.title,
      subject: {
        ...classDetails,
        title: classDetails.subject.title,
      },
      date: start_time,
      duration_seconds: duration,
    };
    setSelectedClassDetails(modifyValue);
  };

  //function is used to open the edit modal & set selected class info.
  const handleOpenModalAndSetClassInfo = (classDetails, action) => {
    setIsOpen(true);
    setSelectedAction(action);
    if (action === Action.RESCHEDULE) {
      modifyClassPayload(classDetails);
    } else if (action === Action.DELETE || action === Action.CANCEL) {
      setSelectedClassDetails(classDetails);
    }
  };

  //below function is used to check the class is live or not
  function checkOneMinuteRemaining(classData) {
    const startDate = moment(classData.start_time * 1000);
    const endDate = moment(classData.end_time * 1000);
    const currentDate = moment(moment().unix() * 1000);

    const classStartTime = moment
      .unix(classData.start_time)
      .subtract(1, "minutes");

    const formattedEndDate = moment(endDate).format("M/D/YYYY");
    const formattedCurrentDate = moment(currentDate).format("M/D/YYYY");

    const timeDuration = classData.duration_seconds / 60;
    const classEndTime = moment(startDate).add(timeDuration, "minutes");

    if (
      currentDate.isSameOrAfter(startDate, "date") &&
      moment(formattedCurrentDate).isSameOrBefore(
        moment(formattedEndDate),
        "dates"
      )
    ) {
      if (
        moment(currentDate, "LT").isSame(
          moment(classStartTime, "LT"),
          "minutes"
        ) &&
        !moment(currentDate, "LT").isAfter(
          moment(classEndTime, "LT"),
          "minutes"
        )
      ) {
        return false;
      } else if (
        moment(currentDate, "LT").isSame(moment(classEndTime, "LT"), "minutes")
      ) {
        return true;
      } else if (
        moment(currentDate, "LT").isAfter(
          moment(classStartTime, "LT"),
          "minutes"
        ) &&
        !moment(currentDate, "LT").isAfter(
          moment(classEndTime, "LT"),
          "minutes"
        )
      ) {
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  return (
    <>
      <div className="mt-4 mb-3 flex justify-start items-center gap-3">
        <button
          onClick={() => {
            handleFilter(TabAction.TODAY, false);
          }}
          className={`${
            activeTab === TabAction.TODAY &&
            "font-['Poppins-semibold'] bg-gray-100 text-black rounded-lg p-1"
          }`}
        >
          Today
        </button>
        <button
          onClick={() => {
            handleFilter(TabAction.TOMORROW, false);
          }}
          className={`${
            activeTab === TabAction.TOMORROW &&
            "font-['Poppins-semibold'] bg-gray-100 text-black rounded-lg p-1"
          }`}
        >
          Tomorrow
        </button>
        <button
          onClick={() => {
            handleFilter(TabAction.ALL, false);
          }}
          className={`${
            activeTab === TabAction.ALL &&
            "font-['Poppins-semibold'] bg-gray-100 text-black rounded-lg p-1"
          }`}
        >
          All
        </button>
      </div>
      <div className="font-['Poppins'] mx-auto text-lg mt-4">
        View your classes
      </div>
      {filterData?.length > 0 ? (
        _.orderBy(filterData, ["start_time"], ["asc"])?.map((vClass, index) => (
          <div className="mb-8 rounded-lg bg-[#F4F4F4] w-[800px]" key={index}>
            <div className="w-full bg-[#F4F4F4] mx-auto rounded-lg text-left mb-0 text-lg flex flex-col">
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="bg-white z-10">
                      <div className="shadow z-10 rounded-lg p-2 text-sm flex items-center">
                        <span className="grid grid-rows-2 lg:grid-cols-2 lg:grid-rows-1 flex-grow">
                          <span className="grid grid-cols-2 text-sm mt-1">
                            <span className="mr-3 text-sm break-all md:text-lg flex items-center">
                              <i className="text-zinc-400 icon icon-building text-2xl mr-0.5 md:mr-3"></i>
                              <span className="text-sm ml-2.5 break-all">
                                {vClass?.title}
                              </span>
                            </span>
                            <span className="mr-3 text-sm md:text-lg flex items-center">
                              <i className="text-zinc-400 icon icon-book-open text-2xl mr-0.5 md:mr-3"></i>
                              <p className="truncate text-sm">
                                {vClass?.subject?.title}
                              </p>
                            </span>
                          </span>
                          <span className="grid grid-cols-2 mt-1">
                            <span className="mr-3 text-sm md:text-lg flex items-center">
                              <i className="text-zinc-400 icon icon-clock text-2xl mr-0.5 md:mr-3"></i>
                              <span className="text-sm">
                                {moment.unix(vClass?.start_time).format("LT")} -{" "}
                                {moment
                                  .unix(
                                    vClass?.start_time +
                                      vClass?.duration_seconds
                                  )
                                  .format("LT")}
                              </span>
                            </span>
                            <div className="flex items-center justify-between">
                              <span className="mr-3 text-sm flex items-center">
                                <i className="text-zinc-400 icon icon-users text-2xl mr-0.5 md:mr-3"></i>
                                <span className="text-sm">
                                  {vClass.student_count}
                                </span>
                              </span>
                              <div className="flex items-center gap-2 mr-4">
                                <button
                                  type="button"
                                  disabled={checkOneMinuteRemaining(vClass)}
                                  onClick={() =>
                                    !isMeetingStarting &&
                                    handleStartClick(vClass.id, index)
                                  }
                                  className={`bg-${
                                    checkOneMinuteRemaining(vClass)
                                      ? "gray-300 text-black "
                                      : "black text-white"
                                  } text-sm rounded-lg flex px-4 py-2 border-none outline-0`}
                                >
                                  {index === selectedRow &&
                                    isMeetingStarting && (
                                      <Partials.PartialLoading />
                                    )}
                                  Start
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    history.push(`/teacher/dashboard/${vClass.id}`)
                                  }
                                  className="bg-black text-white text-sm rounded-lg flex justify-center px-4 py-2 border-none outline-0 w-24"
                                >
                                  Dashboard
                                </button>
                                <img
                                  src={Toggle}
                                  alt=""
                                  draggable={false}
                                  className={
                                    !open ? "h-4 w-4 rotate-180" : "h-4 w-4"
                                  }
                                />
                              </div>
                            </div>
                          </span>
                        </span>
                      </div>
                    </Disclosure.Button>

                    <Disclosure.Panel className="text-gray-500 bg-[#F4F4F4] rounded-lg">
                      <div className="px-3 py-1">
                        <div className="flex flex-row justify-between p-2">
                          <span className="text-md text-black">Details</span>
                          <div className="flex gap-1">
                            <span className="text-md h-6 w-6">
                              <button
                                type="button"
                                onClick={() =>
                                  goToEditVirtualClass(vClass.id)
                                }
                              >
                                <i className="icon icon-edit-2"></i>
                              </button>
                            </span>
                            <span className="text-md h-6 w-6">
                              <button
                                type="button"
                                onClick={() =>
                                  handleOpenModalAndSetClassInfo(
                                    vClass,
                                    Action.RESCHEDULE
                                  )
                                }
                              >
                                <i className="icon icon-spinner"></i>
                              </button>
                            </span>
                            <span className="text-md h-6 w-6">
                              <button
                                type="button"
                                onClick={() =>
                                  handleOpenModalAndSetClassInfo(
                                    vClass,
                                    Action.CANCEL
                                  )
                                }
                              >
                                <i className="icon icon-cancel-circle"></i>
                              </button>
                            </span>
                            <span className="text-md h-6 w-6">
                              <button
                                type="button"
                                onClick={() =>
                                  handleOpenModalAndSetClassInfo(
                                    vClass,
                                    Action.DELETE
                                  )
                                }
                              >
                                <i className="icon icon-bin"></i>
                              </button>
                            </span>
                          </div>
                        </div>
                        <div className="p-2">
                          <div className="text-sm text-black text-left pl-2">
                            {parse(vClass.description)}
                          </div>
                        </div>
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
          </div>
        ))
      ) : (
        <div className="mt-5 w-100 text-center font-['Poppins-semibold']">
          <span className="text-xl">No class Available</span>
        </div>
      )}

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto"
          onClose={handleCloseModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
                <Dialog.Title
                  as="h3"
                  className="text-xl font-['Poppins-Bold'] leading-6 text-gray-900"
                >
                  {selectedAction === Action.RESCHEDULE && "Reschedule"}
                  {selectedAction === Action.DELETE && "Delete"}
                  {selectedAction === Action.CANCEL && "Cancel"} class
                </Dialog.Title>
                <div className="mt-2">
                  {selectedAction === Action.RESCHEDULE && (
                    <Blocks.BlockRescheduleClass
                      handleCloseModal={handleCloseModal}
                      classDetails={selectedClassDetails}
                    />
                  )}
                  {selectedAction === Action.DELETE && (
                    <Blocks.BlockDeleteClass
                      handleCloseModal={handleCloseModal}
                      classDetails={selectedClassDetails}
                    />
                  )}
                  {selectedAction === Action.CANCEL && (
                    <Blocks.BlockCancelClass
                      handleCloseModal={handleCloseModal}
                      classDetails={selectedClassDetails}
                    />
                  )}
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default BlockTeacherLiveClass;